import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    
    try {
      await login(formData);
      navigate('/chat');
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.status === 401) {
        setErrors({ submit: 'Email ou mot de passe incorrect' });
      } else if (error.response?.status === 400) {
        setErrors({ submit: 'Données invalides' });
      } else if (error.response?.status === 429) {
        setErrors({ submit: 'Trop de tentatives. Réessayez dans quelques minutes.' });
      } else if (error.response?.status >= 500) {
        setErrors({ submit: 'Problème serveur temporaire. Réessayez dans un instant.' });
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        setErrors({ submit: 'Problème de connexion. Vérifiez votre internet.' });
      } else {
        setErrors({ submit: 'Erreur de connexion. Veuillez réessayer.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@400;700&display=swap');`}
      </style>
      
      <div 
        className="min-h-screen flex flex-col px-4 py-6 relative overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #B8E0E8 0%, #C5E8D8 50%, #D4EED0 100%)'
        }}
      >
        {/* Header avec bulle et mascotte */}
        <div className="flex justify-between items-start mb-4">
          {/* Bulle "Dis Maman !" */}
          <Link to="/" className="relative">
            <svg 
              viewBox="0 0 200 80" 
              className="w-[140px] sm:w-[180px] h-auto"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
            >
              <path 
                d="M 15,40 
                   C 15,15 35,5 70,5 
                   C 110,3 150,8 175,18 
                   C 192,25 197,40 192,55 
                   C 188,68 165,75 130,78 
                   C 95,81 55,78 30,68
                   L 10,82
                   L 20,65
                   C 13,60 15,50 15,40 Z"
                fill="white"
                stroke="#374151"
                strokeWidth="2.5"
              />
            </svg>
            <p 
              className="absolute top-1/2 left-1/2 text-xl sm:text-2xl font-bold text-gray-800 whitespace-nowrap"
              style={{ 
                fontFamily: '"Caveat", cursive',
                transform: 'translate(-52%, -50%) rotate(-1deg)'
              }}
            >
              Dis Maman !
            </p>
          </Link>

          {/* Mascotte */}
          <div className="relative -mt-2 -mr-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_7b59aa73-04f7-469e-b3b0-3b5db031400b/artifacts/0zxv9crx_02D61451-5FF5-40EF-9C50-17F42BA59024.png"
              alt="Mascotte"
              className="h-[120px] sm:h-[160px] object-contain"
              style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.15))' }}
            />
          </div>
        </div>

        {/* Formulaire dans une carte blanche */}
        <div className="bg-white rounded-3xl p-6 shadow-lg mx-auto w-full max-w-md">
          {/* Header du formulaire */}
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">👋</div>
            <h1 
              className="text-3xl font-bold text-gray-800 mb-1"
              style={{ fontFamily: '"Caveat", cursive' }}
            >
              Connexion
            </h1>
            <p className="text-gray-500 text-sm">
              Bon retour parmi nous !
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1 text-center">
                ✉️ Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="votre.email@example.com"
                className={`w-full px-4 py-3 rounded-xl border-2 ${
                  errors.email ? 'border-red-300' : 'border-gray-200'
                } focus:border-teal-400 focus:outline-none transition-colors bg-gray-50`}
                data-testid="email-input"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1 text-center">
                🔒 Mot de passe
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Votre mot de passe"
                className={`w-full px-4 py-3 rounded-xl border-2 ${
                  errors.password ? 'border-red-300' : 'border-gray-200'
                } focus:border-teal-400 focus:outline-none transition-colors bg-gray-50`}
                data-testid="password-input"
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>

            {/* Lien mot de passe oublié */}
            <div className="text-center">
              <Link 
                to="/forgot-password" 
                className="text-teal-600 text-sm hover:text-teal-700"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ? 🔑
              </Link>
            </div>

            {/* Erreur globale */}
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center">
                {errors.submit}
              </div>
            )}

            {/* Bouton submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 px-6 rounded-2xl text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70"
              style={{
                background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)'
              }}
              data-testid="login-submit-btn"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Connexion...
                </>
              ) : (
                <>
                  🚀 Se connecter
                </>
              )}
            </button>
          </form>

          {/* Lien inscription */}
          <div className="text-center mt-4">
            <p className="text-gray-600 text-sm">
              Pas encore de compte ?{' '}
              <Link 
                to="/register" 
                className="text-teal-600 font-bold underline hover:text-teal-700"
                data-testid="register-link"
              >
                Inscrivez-vous !
              </Link>
              {' '}🎉
            </p>
          </div>
        </div>

        {/* Retour accueil */}
        <div className="text-center mt-4">
          <Link 
            to="/" 
            className="text-gray-600 text-sm hover:text-gray-800"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
